import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import {
    memberJobsQueryOptions,
    useCreateMemberJobMutation,
    useDeleteMemberJobMutation,
    useUpdateMemberJobMutation,
} from '../../api/mem/memberJobQueryOptions'

import LoadProgress from '@/components/LoadProgress'
import { Grid } from '@mui/material'

import { getGridSingleSelectOperators, GridColDef } from '@mui/x-data-grid-premium'

import {
    CreateMemberJobType,
    defaultMemberJob,
    memberJobSchema,
    MemberJobType,
    UpdateMemberJobType,
} from '@/api/mem/memberJob'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import { sectionsAvailableQueryOptions } from '@/api/sectionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import useResponsive from '@/components/hooks/useResponsive'
import RenderJobOrganization from '@/components/mui-x/datagrid/renderer/RenderJobOrganization'
import ScoregCheckbox from '@/components/mui/ScoregCheckbox'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectJob from '@/components/select/SelectJob'
import SelectOrganization from '@/components/select/SelectOrganization'
import SelectSection from '@/components/select/SelectSection'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import { TextField } from '@mui/material'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/jobs',
)({
    component: MemberComponent,
})

function MemberComponent() {
    const params = Route.useParams()
    const memberJobsQuery = useSuspenseQuery(
        memberJobsQueryOptions(params.memberUuid),
    )
    const memberJobs = memberJobsQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'MemberJob',
        ]),
    )
    const permissions = permissionsQuery.data

    const sectionsQuery = useSuspenseQuery(sectionsAvailableQueryOptions())
    const sections = sectionsQuery.data;

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'MemberJob' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const crud = useDetailCrudEvents<MemberJobType>('memberJob')
    const createCrud = useDetailCrudEvents<CreateMemberJobType>('memberJob')
    const updateCrud = useDetailCrudEvents<UpdateMemberJobType>('memberJob')

    const createMemberJobMutation = useCreateMemberJobMutation(
        params.memberUuid,
        createCrud.onCreated,
        createCrud.onError,
    )
    const updateMemberJobMutation = useUpdateMemberJobMutation(
        params.memberUuid,
        updateCrud.onUpdated,
        updateCrud.onError,
    )
    const deleteMemberJobMutation = useDeleteMemberJobMutation(
        params.memberUuid,
        crud.onDeleted,
        crud.onError,
    )

    const basicGridColumns = useBasicGridColumns()
    const sessionOrganization = useSelector(getOrganization)

    const onSubmit = (data: MemberJobType) => {
        createMemberJobMutation.mutate(data)
    }

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    columns.push(basicGridColumns.activeColumn)
    if (xsmall || small || medium) {
        columns.push({
            field: 'job_organization',
            valueGetter: (_value, row) => `${row}`,
            headerName: 'Funktion/Organisation',
            minWidth: 150,
            renderCell: RenderJobOrganization,

        })
    }
    if (large || xlarge) {
        columns.push(
            basicGridColumns.jobNameColumn,
            basicGridColumns.organizationNameColumn,
        )
        columns.push(
            {
                ...basicGridColumns.sectionAbbreviationColumn,
                type: 'singleSelect',
                getOptionLabel: (option: any) => option.abbreviation,
                getOptionValue: (option: any) => option.uuid,
                valueGetter: (_value, row) => row.section?.uuid,
                valueFormatter: (_value, row) => row.section?.abbreviation,
                valueSetter: (value, row) => {
                    return { ...row, section: { uuid: value } }
                },
                filterOperators: getGridSingleSelectOperators().filter((operator) => operator.value === 'isAnyOf'),
                valueOptions: sections,
                editable: true,
            }
        )
    }
    if (small) {
        columns.push(basicGridColumns.validPeriodColumn)
    }
    if (medium || large || xlarge) {
        columns.push(
            basicGridColumns.startDateColumn,
            basicGridColumns.endDateColumn,
        )
        columns.push(
            {
                field: 'unknownPeriod',
                headerName: t('property.general.unknownPeriod'),
                type: 'boolean',
                editable: true,
            }
        )
    }

    columns.push(basicGridColumns.descriptionColumn)

    const createDetail = useCreateDetail<MemberJobType>({
        onSubmit,
        resolver: yupResolver<MemberJobType, any, any>(memberJobSchema),
        defaultValues: {
            ...defaultMemberJob,
            member: {
                uuid: params.memberUuid,
            },
            organization: {
                uuid: sessionOrganization.uuid,
            },
        },
        title: t('action.assign', { entity: t('entity.job') }),
    })

    const editGrid = useEditGrid<MemberJobType>({
        defaultValues: defaultMemberJob,
        updateMutation: updateMemberJobMutation,
        deleteMutation: deleteMemberJobMutation,
        records: memberJobs,
        columns: columns,
        title: t('entity.job'),
    })

    if (memberJobsQuery.isLoading || memberJobsQuery.isFetching) {
        return <LoadProgress />
    }
    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        {/*JSON.stringify({
        xs: xsmall,
        sm: small,
        md: medium, 
        lg: large,
        xl: xlarge,
      })*/}
        {createable && (
            <CreateDetailAccordion {...createDetail}>
                <Grid container spacing={2}>
                    <input
                        type="hidden"
                        defaultValue={params.memberUuid}
                        {...createDetail.form.register('member.uuid')}
                    />
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectJob control={createDetail.form.control} name="job.uuid" />
                    </Grid>
                    {(sessionOrganization.organizationLevel === 'DIVISION' ||
                        sessionOrganization.organizationLevel === 'FEDERATION') && (
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 4
                                }}>
                                <SelectOrganization control={createDetail.form.control} name="organization.uuid" includeCurrentOrganization />
                            </Grid>
                        )}
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectSection
                            control={createDetail.form.control}
                            name="section.uuid"
                            optional={true}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregDatePicker
                            name="startDate"
                            defaultValue={dayjs(defaultMemberJob.startDate)}
                            disabled={false}
                            control={createDetail.form.control}
                            label={t('property.general.startDate')}
                            maxDate={dayjs(createDetail.form.getValues('endDate'))}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregDatePicker
                            name="endDate"
                            defaultValue={null}
                            disabled={false}
                            control={createDetail.form.control}
                            label={t('property.general.endDate')}
                            minDate={dayjs(createDetail.form.getValues('startDate'))}
                        />
                    </Grid>
                    <></>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregCheckbox defaultValue={false} name="unknownPeriod" control={createDetail.form.control} label={t('property.general.unknownPeriod')} />
                    </Grid>
                </Grid>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 6
                        }}>
                        <TextField
                            {...createDetail.form.register('description')}
                            label={t('property.general.description')}
                            multiline
                            rows={2} />
                    </Grid>
                </Grid>
            </CreateDetailAccordion>
        )}
        <EditGrid {...editGrid} detailLink="/member/$memberUuid/memberjob/$uuid" parentUuid={(entity) => entity.member.uuid} />
    </>);
}
